import React, { FC } from "react";
import { ExchangeReturnByDate } from "../ExchangeReturnByDate";
import { ImageAndQuantity } from "../ImageAndQuantity";
import { Price } from "../Price";
import { Subtotal } from "../Subtotal";
import { DiscountDetails, Discounts } from "../Discounts";
import { BundleDetails } from "../BundleDetails";
import { CustomizationDetails } from "../CustomizationDetails";
import { SubscriptionDetails } from "../SubscriptionDetails";
import { Error } from "../Error";
import {
  BundleComponent,
  Localization,
  CustomizationFragment,
  SubscriptionMetadata,
  CheckoutOrderSummaryLocalizations,
} from "../../../../types/graphql/generated";
import { ItemErrorMsg } from "../../../../types/cart";
import {
  StyledAlert,
  StyledCartItem,
  StyledDetailsWrapper,
  StyledHeading,
  StyledTopDetails,
} from "./index.styled";

export interface CartItemProps {
  bundleItems?: BundleComponent[] | null;
  customizations?: CustomizationFragment[] | null;
  discounts: DiscountDetails[];
  errorMsgs?: ItemErrorMsg[];
  gtin?: string;
  imgUrl?: string | null;
  itemNotifications?: string[] | null;
  perUnitPrice: string;
  quantity: number;
  returnDueDate?: string;
  strikethroughUnitPrice?: string | null;
  subscription?: SubscriptionMetadata | null;
  subtotal: string;
  title?: string | null;
  componentsLocalization?: Localization["components"];
  orderSummaryLocalization?: NonNullable<CheckoutOrderSummaryLocalizations> | null;
}

export const CartItem: FC<CartItemProps> = ({
  bundleItems,
  customizations,
  discounts,
  errorMsgs,
  gtin,
  imgUrl,
  itemNotifications,
  perUnitPrice,
  quantity,
  returnDueDate,
  strikethroughUnitPrice,
  subscription,
  subtotal,
  title,
  componentsLocalization,
  orderSummaryLocalization,
}) => {
  const { subscriptionIntervalInDays } = subscription || {};
  const isBundle = !!bundleItems?.length;
  const hasCustomizations = !!customizations?.length;
  const isSubscription = !!subscriptionIntervalInDays;
  const hasErrors = !!errorMsgs?.length;

  return (
    <StyledCartItem data-testid="cart-item" data-gtin={gtin}>
      <ImageAndQuantity
        productTitle={title || ""}
        itemImgSrc={imgUrl || ""}
        quantity={quantity}
      />
      <StyledDetailsWrapper>
        <StyledTopDetails>
          <StyledHeading level="h2" size="xs">
            {title || ""}
          </StyledHeading>
          {subtotal && <Subtotal subtotal={subtotal} />}
        </StyledTopDetails>
        <Price
          strikethroughUnitPrice={strikethroughUnitPrice}
          perUnitPrice={perUnitPrice}
          priceComponentLocalization={componentsLocalization?.price}
        />
        {itemNotifications?.map((itemNotification, i) => (
          <StyledAlert bold key={`item-notification-${i}`} type="info">
            {itemNotification}
          </StyledAlert>
        ))}
        {isBundle && (
          <BundleDetails
            bundleItems={bundleItems}
            localization={orderSummaryLocalization}
          />
        )}
        {!isBundle && hasCustomizations && (
          <CustomizationDetails customizations={customizations} />
        )}
        {isSubscription && <SubscriptionDetails subscription={subscription} />}
        {!isSubscription && returnDueDate && (
          <ExchangeReturnByDate
            returnDueDate={returnDueDate}
            componentLocalization={componentsLocalization?.exchangeReturnByDate}
          />
        )}
        <Discounts
          discounts={discounts}
          localization={orderSummaryLocalization}
        />
        {hasErrors && <Error errorMsgs={errorMsgs} />}
      </StyledDetailsWrapper>
    </StyledCartItem>
  );
};
